<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import { buildContactTierOptions } from 'dashboard/components-next/Contacts/contactTiers';
import { PREFERRED_LANGUAGE_OPTIONS } from 'dashboard/components-next/Contacts/preferredLanguages';
import ContactPreferenceDefinitionsAPI from 'dashboard/api/contactPreferenceDefinitions';
import {
  buildPreferencesPayload,
  compactPreferenceList,
  preferenceDefinitionOptions,
  preferencesFromRows,
} from 'dashboard/helper/contactPreferences';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
  showEditSectionTitle: {
    type: Boolean,
    default: true,
  },
  showBio: {
    type: Boolean,
    default: true,
  },
  showSocialLinks: {
    type: Boolean,
    default: true,
  },
  sheetPresentation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  TIER: { field: 'additionalAttributes.tier' },
  PREFERRED_CHANNEL: { field: 'additionalAttributes.preferredChannel' },
  PREFERRED_LANGUAGE: { field: 'additionalAttributes.preferredLanguage' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const createDefaultState = () => ({
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  preferences: {},
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    tier: '',
    preferredChannel: 'whatsapp',
    preferredLanguage: 'fr',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
});

const state = reactive(createDefaultState());

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const preferenceDefinitions = ref([]);

const availablePreferenceDefinitions = computed(() => {
  const existingValueKeys = new Set(
    Object.keys(state.preferences).filter(key =>
      state.preferences[key]?.toString().trim()
    )
  );

  return preferenceDefinitions.value.filter(
    definition => definition.active || existingValueKeys.has(definition.key)
  );
});

const buildContactPayload = () => {
  const { firstName, lastName, preferences, ...stateWithoutNames } = state;
  return {
    ...stateWithoutNames,
    contact_preferences: buildPreferencesPayload(
      availablePreferenceDefinitions.value,
      preferences
    ),
  };
};

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    contactPreferences = [],
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    tier = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};
  const preferredChannel =
    additionalAttributes.preferredChannel ||
    additionalAttributes.preferred_channel ||
    'whatsapp';
  const preferredLanguage =
    additionalAttributes.preferredLanguage ||
    additionalAttributes.preferred_language ||
    '';

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    preferences: preferencesFromRows(contactPreferences),
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      tier,
      preferredChannel,
      preferredLanguage,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const preferredChannelOptions = computed(() => [
  { label: t('PURCHASE_PROJECTS.CHANNELS.WHATSAPP'), value: 'whatsapp' },
  { label: t('PURCHASE_PROJECTS.CHANNELS.SMS'), value: 'sms' },
  { label: t('PURCHASE_PROJECTS.CHANNELS.EMAIL'), value: 'email' },
]);

const contactTierOptions = computed(() => buildContactTierOptions(t));

const preferredLanguageOptions = computed(() => PREFERRED_LANGUAGE_OPTIONS);

const editDetailsForm = computed(() =>
  [
    {
      key: 'FIRST_NAME',
      label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.FIRST_NAME.LABEL'),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.FIRST_NAME.PLACEHOLDER'
      ),
    },
    {
      key: 'LAST_NAME',
      label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.LAST_NAME.LABEL'),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.LAST_NAME.PLACEHOLDER'
      ),
    },
    {
      key: 'EMAIL_ADDRESS',
      label: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL_ADDRESS.LABEL'
      ),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER'
      ),
    },
    {
      key: 'PHONE_NUMBER',
      label: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.LABEL'
      ),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.PLACEHOLDER'
      ),
    },
    {
      key: 'CITY',
      label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.CITY.LABEL'),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.CITY.PLACEHOLDER'
      ),
    },
    {
      key: 'COUNTRY',
      label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.COUNTRY.LABEL'),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.COUNTRY.PLACEHOLDER'
      ),
    },
    {
      key: 'TIER',
      label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.TIER.LABEL'),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.TIER.PLACEHOLDER'
      ),
    },
    {
      key: 'PREFERRED_CHANNEL',
      label: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PREFERRED_CHANNEL.LABEL'
      ),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PREFERRED_CHANNEL.PLACEHOLDER'
      ),
    },
    {
      key: 'PREFERRED_LANGUAGE',
      label: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PREFERRED_LANGUAGE.LABEL'
      ),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PREFERRED_LANGUAGE.PLACEHOLDER'
      ),
    },
    {
      key: 'BIO',
      label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.BIO.LABEL'),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.BIO.PLACEHOLDER'
      ),
    },
    {
      key: 'COMPANY_NAME',
      label: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.COMPANY_NAME.LABEL'
      ),
      placeholder: t(
        'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.COMPANY_NAME.PLACEHOLDER'
      ),
    },
  ].filter(item => props.showBio || item.key !== 'BIO')
);

const preferencesForm = computed(() =>
  availablePreferenceDefinitions.value.map(definition => ({
    key: definition.key,
    label: definition.label,
    fieldType: definition.field_type,
    options: preferenceDefinitionOptions(definition),
    placeholder: ['list', 'list_with_custom'].includes(definition.field_type)
      ? t('CONTACTS_LAYOUT.CARD.PREFERENCES.FORM.LIST_PLACEHOLDER')
      : t('CONTACTS_LAYOUT.CARD.PREFERENCES.FORM.TEXT_PLACEHOLDER'),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

const getFieldConfig = key => {
  return FORM_CONFIG[key];
};

const getValueByPath = path => {
  return path.split('.').reduce((value, segment) => value?.[segment], state);
};

const setValueByPath = (path, value) => {
  const segments = path.split('.');
  const target = segments
    .slice(0, -1)
    .reduce((nestedValue, segment) => nestedValue?.[segment], state);

  if (target) {
    target[segments.at(-1)] = value;
  }
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = getFieldConfig(key)?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      return getValueByPath(field)?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        setValueByPath(field, value);
      }

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        emit('update', buildContactPayload());
      }
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const fieldWrapperClass = computed(() =>
  props.sheetPresentation ? 'flex flex-col gap-1' : ''
);

const sectionClass = computed(() =>
  props.sheetPresentation
    ? 'flex flex-col gap-3 bg-n-solid-1 px-4 py-4'
    : 'flex flex-col items-start gap-2'
);

const sectionTitleClass = computed(() =>
  props.sheetPresentation
    ? 'text-sm font-semibold text-n-slate-12'
    : 'py-1 text-sm font-medium text-n-slate-12'
);

const fieldsGridClass = computed(() =>
  props.sheetPresentation
    ? 'grid w-full grid-cols-1 gap-3 sm:grid-cols-2'
    : 'grid w-full grid-cols-1 gap-4 sm:grid-cols-2'
);

const fieldLabelClass = computed(() =>
  props.sheetPresentation ? 'text-xs font-medium text-n-slate-10' : 'sr-only'
);

const inputClass = computed(() =>
  props.sheetPresentation
    ? 'h-9 !bg-n-alpha-1 !px-3 !py-2 !outline-transparent'
    : `h-8 !pt-1 !pb-1 ${
        !props.isDetailsView
          ? '[&:not(.error,.focus)]:!outline-transparent'
          : ''
      }`
);

const comboBoxClass = computed(() => ({
  '[&>div>button]:h-9 [&>div>button]:!bg-n-alpha-1 [&>div>button:not(.focused)]:!outline-transparent':
    props.sheetPresentation,
  '[&>div>button]:h-8': !props.sheetPresentation,
  '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
    !props.sheetPresentation && !props.isDetailsView,
  '[&>div>button]:!bg-n-alpha-black2':
    !props.sheetPresentation && props.isDetailsView,
}));

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', buildContactPayload());
};

const handlePreferredChannelSelection = value => {
  state.additionalAttributes.preferredChannel = value || 'whatsapp';
  emit('update', buildContactPayload());
};

const handleContactTierSelection = value => {
  state.additionalAttributes.tier = value || '';
  emit('update', buildContactPayload());
};

const handlePreferredLanguageSelection = value => {
  state.additionalAttributes.preferredLanguage = value || '';
  emit('update', buildContactPayload());
};

const updatePreference = async (key, value) => {
  state.preferences[key] = value;
  const isFormValid = await v$.value.$validate();
  if (isFormValid) {
    emit('update', buildContactPayload());
  }
};

const preferenceInputValue = item => {
  const value = state.preferences[item.key];
  if (['list', 'list_with_custom'].includes(item.fieldType)) {
    return compactPreferenceList(value);
  }
  if (Array.isArray(value)) return value.join(', ');

  return value || '';
};

const fetchPreferenceDefinitions = async () => {
  try {
    const response = await ContactPreferenceDefinitionsAPI.getActive();
    preferenceDefinitions.value = response.data || [];
  } catch (error) {
    preferenceDefinitions.value = [];
  }
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, createDefaultState());
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

onMounted(fetchPreferenceDefinitions);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div
    class="flex flex-col"
    :class="sheetPresentation ? 'gap-2 bg-n-alpha-1 py-2' : 'gap-6'"
  >
    <div :class="sectionClass">
      <span v-if="showEditSectionTitle" :class="sectionTitleClass">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div :class="fieldsGridClass">
        <template v-for="item in editDetailsForm" :key="item.key">
          <div :class="fieldWrapperClass">
            <label :class="fieldLabelClass">
              {{ item.label }}
              <span
                v-if="item.key === 'FIRST_NAME'"
                class="ml-0.5 text-n-ruby-9 after:content-['*']"
                aria-hidden="true"
              />
            </label>
            <ComboBox
              v-if="item.key === 'COUNTRY'"
              v-model="state.additionalAttributes.countryCode"
              :options="countryOptions"
              :placeholder="item.placeholder"
              :class="comboBoxClass"
              @update:model-value="handleCountrySelection"
            />
            <PhoneNumberInput
              v-else-if="item.key === 'PHONE_NUMBER'"
              v-model="getFormBinding(item.key).value"
              :placeholder="item.placeholder"
              :show-border="!sheetPresentation && isDetailsView"
              :class="{
                '[&>div]:!h-9 [&>div]:!bg-n-alpha-1 [&>div]:!outline-transparent':
                  sheetPresentation,
              }"
            />
            <ComboBox
              v-else-if="item.key === 'PREFERRED_CHANNEL'"
              v-model="state.additionalAttributes.preferredChannel"
              :options="preferredChannelOptions"
              :placeholder="item.placeholder"
              :class="comboBoxClass"
              @update:model-value="handlePreferredChannelSelection"
            />
            <ComboBox
              v-else-if="item.key === 'TIER'"
              v-model="state.additionalAttributes.tier"
              :options="contactTierOptions"
              :placeholder="item.placeholder"
              :search-placeholder="item.placeholder"
              :class="comboBoxClass"
              @update:model-value="handleContactTierSelection"
            />
            <ComboBox
              v-else-if="item.key === 'PREFERRED_LANGUAGE'"
              v-model="state.additionalAttributes.preferredLanguage"
              :options="preferredLanguageOptions"
              :placeholder="item.placeholder"
              :search-placeholder="item.placeholder"
              :class="comboBoxClass"
              @update:model-value="handlePreferredLanguageSelection"
            />
            <Input
              v-else
              v-model="getFormBinding(item.key).value"
              :placeholder="item.placeholder"
              :message-type="getMessageType(item.key)"
              :custom-input-class="inputClass"
              class="w-full"
              @input="
                isValidationField(item.key) &&
                  v$[getValidationKey(item.key)].$touch()
              "
              @blur="
                isValidationField(item.key) &&
                  v$[getValidationKey(item.key)].$touch()
              "
            />
          </div>
        </template>
      </div>
    </div>
    <div v-if="preferencesForm.length" :class="sectionClass">
      <span :class="sectionTitleClass">
        {{ t('CONTACTS_LAYOUT.CARD.PREFERENCES.TITLE') }}
      </span>
      <div :class="fieldsGridClass">
        <div
          v-for="item in preferencesForm"
          :key="item.key"
          :class="fieldWrapperClass"
        >
          <label :class="fieldLabelClass">{{ item.label }}</label>
          <TagMultiSelectComboBox
            v-if="['list', 'list_with_custom'].includes(item.fieldType)"
            :model-value="preferenceInputValue(item)"
            :options="item.options"
            :placeholder="item.placeholder"
            :allow-custom="item.fieldType === 'list_with_custom'"
            :class="comboBoxClass"
            @update:model-value="value => updatePreference(item.key, value)"
          />
          <Input
            v-else
            :model-value="preferenceInputValue(item)"
            :placeholder="item.placeholder"
            type="text"
            :custom-input-class="inputClass"
            class="w-full"
            @update:model-value="value => updatePreference(item.key, value)"
          />
        </div>
      </div>
    </div>
    <div v-if="showSocialLinks" :class="sectionClass">
      <span :class="sectionTitleClass">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', buildContactPayload())"
          />
        </div>
      </div>
    </div>
  </div>
</template>
